import { AnimatePresence, motion, useDragControls } from "motion/react";
import { Dialog, Modal, ModalOverlay } from "react-aria-components";
import { Check } from "lucide-react";
import { KitButton } from "@/app/routes/dashboard/components/kit/Button";
import { useCoShellPortal } from "@/app/routes/dashboard/CoShellPortalContext";
import type { TrendType } from "@/types/trend.type";
import { TYPE_LABEL } from "../components/trendMeta";
import type { TrendFilterGroup } from "../trendFilters";
import { SPRING_SMOOTH } from "@/lib/motion";

const MotionOverlay = motion.create(ModalOverlay);
const MotionModal = motion.create(Modal);

/** The house spring for something that arrives: critically damped. */

/** A drag down past this far, or this fast, lets the sheet go. */
const DISMISS_OFFSET_PX = 120;
const DISMISS_VELOCITY = 600;

/**
 * The phone's filters: a bottom sheet in thick glass, the same groups and the
 * same state as the desktop's panel, drawn as 44px rows with a check on each
 * picked one. Changes apply as they are made; the footer only closes the
 * sheet, and says what the wall will show in words, never as a count.
 *
 * A react-aria Modal and Dialog, so focus is trapped inside it and Escape and
 * a tap on the scrim both close it. Drag the grabber down to let it go.
 */
export default function TrendFilterSheet({
  open,
  onClose,
  groups,
  canClear,
  onClearAll,
  types,
}: {
  open: boolean;
  onClose: () => void;
  groups: readonly TrendFilterGroup[];
  canClear: boolean;
  onClearAll: () => void;
  /** The kinds picked, which name the footer's action. */
  types: readonly TrendType[];
}) {
  const portal = useCoShellPortal();
  const drag = useDragControls();
  const showLabel =
    types.length === 1 ? `Show ${TYPE_LABEL[types[0]].toLowerCase()} trends` : "Show trends";

  return (
    <AnimatePresence>
      {open ? (
        <MotionOverlay
          key="filter-sheet"
          isOpen
          isDismissable
          onOpenChange={(next) => {
            if (!next) onClose();
          }}
          UNSTABLE_portalContainer={portal ?? undefined}
          className="tf-sheet-scrim"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={SPRING_SMOOTH}
        >
          <MotionModal
            className="tf-sheet"
            initial={{ y: "100%" }}
            animate={{ y: 0 }}
            exit={{ y: "100%" }}
            transition={SPRING_SMOOTH}
            drag="y"
            dragListener={false}
            dragControls={drag}
            dragConstraints={{ top: 0, bottom: 0 }}
            dragElastic={{ top: 0, bottom: 1 }}
            onDragEnd={(_event, info) => {
              if (info.offset.y > DISMISS_OFFSET_PX || info.velocity.y > DISMISS_VELOCITY) onClose();
            }}
          >
            <Dialog aria-label="Filters" className="tf-sheet-dialog">
              <div
                className="tf-sheet-grab"
                onPointerDown={(event) => drag.start(event)}
                aria-hidden="true"
              >
                <span className="tf-grabber" />
              </div>
              <div className="tf-sheet-head">
                <span className="tf-sheet-title">Filters</span>
                <button type="button" className="trend-filter-clear" disabled={!canClear} onClick={onClearAll}>
                  Clear all
                </button>
              </div>
              <div className="tf-sheet-body">
                {groups.map((group) => (
                  <div key={group.key} className="tf-sheet-group" role="group" aria-label={group.caption}>
                    <p className="trend-filter-caption tf-sheet-caption" aria-hidden="true">
                      {group.caption}
                    </p>
                    {group.options.map((option) => (
                      <button
                        key={option.value}
                        type="button"
                        className="tf-sheet-row"
                        aria-pressed={option.selected}
                        disabled={group.disabled}
                        onClick={() => group.pick(option.value)}
                      >
                        <span className="tf-sheet-icon" aria-hidden="true">
                          {option.icon ? <option.icon className="size-[14px]" /> : null}
                        </span>
                        <span className="tf-sheet-label">{option.label}</span>
                        {option.selected ? <Check className="tf-sheet-check" aria-hidden="true" /> : null}
                      </button>
                    ))}
                  </div>
                ))}
              </div>
              <div className="tf-sheet-foot">
                <KitButton variant="primary" size="lg" className="h-12 w-full" onClick={onClose}>
                  {showLabel}
                </KitButton>
              </div>
            </Dialog>
          </MotionModal>
        </MotionOverlay>
      ) : null}
    </AnimatePresence>
  );
}
